import { __assign } from "tslib";
import React, { useContext, useState, useMemo } from 'react';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import Torpedo from '../../Torpedo';
import EditorDeviationReactView from '../Views/Thumb';
import DeviationLink from '../../Duperbrowse/DeviationLink';
import Grid from '../../Grid/Grid';
import DaEditorMonoView from '../Views/Mono';
import MediaService from '../../utils/DeviationMediaService';
import { CropFit, GalleryLayout, } from '../../DaEditor/plugins/plugin-gallery/types';
import ZoomModal from '../../DeviationViews/Full/Image/ZoomModal';
import { getSrcSet } from '../../DeviationViews/Dumbo/Image/_helpers';
import { getDeviationTitle } from '../../DeviationViews/_helpers';
import { DeviationDuperbrowseContext } from '../../Duperbrowse';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import cleanLinkSrc from '../../utils/xss/cleanLinkSrc';
/**
 * IMPORTANT!
 * Currently to support green/mobile we are using inline
 * styles to render editor related content. Do NOT add
 * classes to anything here for now.
 */
var DEFAULT_MOBILE_WIDTH = 375;
var DEFAULT_DESKTOP_WIDTH = 780;
var DeviationWithPossibleLink = function (props) {
    var deviation = props.deviation, children = props.children;
    var _a = useState(false), showFullview = _a[0], setShowFullview = _a[1];
    var fullview = useMemo(function () { return MediaService.getLargestFullview(deviation); }, [deviation]);
    if (deviation.isPublished) {
        return (React.createElement(DeviationLink, { deviation: deviation, title: getDeviationTitle(deviation), className: "gallery-link" }, children));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { onClick: function () {
                setShowFullview(true);
            }, style: { cursor: 'zoom-in' } }, children),
        showFullview && (React.createElement(ZoomModal, { src: cleanLinkSrc(fullview.src), srcSet: getSrcSet(deviation), width: fullview.width, height: fullview.height, isPixel: false, onClose: function () {
                setShowFullview(false);
            } }))));
};
DeviationWithPossibleLink.displayName = 'DeviationWithPossibleLink';
export var Gallery = function (_a) {
    var _b = _a.data, data = _b === void 0 ? [] : _b, _c = _a.layout, layout = _c === void 0 ? GalleryLayout.GRID : _c, width = _a.width, settings = _a.settings, isHtml = _a.isHtml, _d = _a.layoutStyles, layoutStyles = _d === void 0 ? {} : _d;
    var isMobile = useContext(MobileContext);
    var canDuperbrowse = useFlag('yumeruby') && data.every(function (d) { return d.isPublished; });
    // When we only have 1 item and we are not in comments
    // use the single deviation view instead
    if (data.length === 1 && !(settings === null || settings === void 0 ? void 0 : settings.showMediaAlwaysSmall)) {
        var config = {
            alignment: 'right',
            key: 'single',
            showDescription: false,
            showTitle: false,
            size: 'content',
        };
        return (React.createElement(DaEditorMonoView, { config: config, settings: settings, deviation: data[0], width: width, isHtml: !!isHtml }));
    }
    if (canDuperbrowse) {
        return (React.createElement(DeviationDuperbrowseContext, { streamId: data.reduce(function (prev, current) { return "".concat(prev, "-").concat(current.deviationId); }, ''), deviations: data }, layout === 1
            ? renderTorpedo(data, width, isHtml, isMobile, layoutStyles)
            : renderGrid(data, width, isHtml, isMobile, layoutStyles, settings)));
    }
    if (layout === 1) {
        return renderTorpedo(data, width, isHtml, isMobile, layoutStyles);
    }
    return renderGrid(data, width, isHtml, isMobile, layoutStyles, settings);
};
Gallery.displayName = 'EditorGallery';
function renderGrid(deviations, width, isHtml, isMobile, layoutStyles, settings) {
    if (!width || width <= 0) {
        width = isMobile ? DEFAULT_MOBILE_WIDTH : DEFAULT_DESKTOP_WIDTH;
    }
    var getId = function (idx) { return deviations[idx].deviationId; };
    var gridStyles = {};
    var breakpoints = [
        {
            maxWidth: 9999,
            elementWidth: (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.gridItemWidth) || 386,
            elementHeight: (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.gridItemHeight) || 338,
        },
    ];
    // Comments force small media
    if (settings && settings.showMediaAlwaysSmall) {
        breakpoints = [{ maxWidth: 9999, elementWidth: 223, elementHeight: 146 }];
        gridStyles = { maxWidth: '685px' };
    }
    if (layoutStyles.innerAlignment) {
        var settingToCss = {
            left: 'flex-start',
            right: 'flex-end',
            center: 'center',
        };
        gridStyles = {
            display: 'flex',
            justifyContent: settingToCss[layoutStyles.innerAlignment],
        };
    }
    return (React.createElement("div", { style: gridStyles, className: "daeditor-square-grid" },
        React.createElement(Grid, { containerWidth: width, breakpoints: breakpoints, elementCount: deviations.length, spacing: (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.spacing) !== undefined ? layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.spacing : 8, getElementId: getId, enableScrollOptim: false, renderElement: function (element) { return (React.createElement("div", { key: element.index, style: __assign({}, element.style) },
                React.createElement(DeviationWithPossibleLink, { deviation: deviations[element.index] },
                    React.createElement(EditorDeviationReactView, { isHtml: isHtml, deviation: deviations[element.index], width: element.width, height: element.height, allowPreviewUsage: true, noCropping: (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.cropping) === CropFit.FIT ? true : false })))); } })));
}
function renderTorpedo(deviations, width, isHtml, isMobile, layoutStyles) {
    var container = {
        width: width,
    };
    if (!width || width <= 0) {
        width = isMobile ? DEFAULT_MOBILE_WIDTH : DEFAULT_DESKTOP_WIDTH;
    }
    var items = [];
    for (var _i = 0, deviations_1 = deviations; _i < deviations_1.length; _i++) {
        var dev = deviations_1[_i];
        var largest = MediaService.getThumbDimensions(dev);
        items.push({
            id: dev.deviationId,
            width: largest.width,
            height: largest.height,
            deviation: dev,
        });
    }
    var layoutConfig = {
        galleryGutter: true,
        itemSpacingDesktop: 4,
        itemSpacingMobile: 4,
    };
    if ((layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.masonryRowHeight) > 0) {
        layoutConfig = {
            rowSizeDesktop: layoutStyles.masonryRowHeight,
            rowSizeCardDesktop: layoutStyles.masonryRowHeight,
            rowSizeMobile: layoutStyles.masonryRowHeight,
            rowSizeCardMobile: layoutStyles.masonryRowHeight,
            rowSizeMobileLarge: layoutStyles.masonryRowHeight,
        };
    }
    if ((layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.spacing) !== undefined) {
        layoutConfig = __assign(__assign({}, layoutConfig), { itemSpacingDesktop: layoutStyles.spacing, itemSpacingMobile: layoutStyles.spacing });
    }
    var gridStyles = {};
    if (layoutStyles.innerAlignment) {
        var settingToCss = {
            left: 'flex-start',
            right: 'flex-end',
            center: 'center',
        };
        gridStyles = {
            display: 'flex',
            justifyContent: settingToCss[layoutStyles.innerAlignment],
        };
    }
    // Key is to help with live preview as torpedo is heavily update protected
    return (React.createElement("div", { className: "daeditor-torpedo-grid", style: gridStyles },
        React.createElement(Torpedo, { key: "torpedo-".concat(layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.cropping), layoutConfig: layoutConfig, items: items, container: container, enableScrollVisibilityOptimisation: false, renderItem: function (props, item, itemStyle) {
                var deviation = item.dto.deviation;
                var itemWidth = item.width, itemHeight = item.height;
                return (React.createElement("div", { className: "daeditor-torpedo-grid-item", key: "torpedo-".concat(deviation.deviationId), style: __assign({ width: itemWidth, height: itemHeight }, itemStyle) },
                    React.createElement(DeviationWithPossibleLink, { deviation: deviation },
                        React.createElement(EditorDeviationReactView, { isHtml: isHtml, deviation: deviation, width: itemWidth, height: itemHeight, allowPreviewUsage: true, noCropping: (layoutStyles === null || layoutStyles === void 0 ? void 0 : layoutStyles.cropping) === CropFit.FIT ? true : false }))));
            } })));
}
//# sourceMappingURL=Gallery.js.map